import { useHistory } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import React, { useEffect, useState, Suspense, useRef } from 'react';
import {
  Button,
  ConfigProvider,
  Dropdown,
  Row,
  Table,
  Form,
  Tag,
  Tooltip,
} from 'antd';
import trTR from 'antd/es/locale/tr_TR';
import {
  MoreOutlined,
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { getFilteredCourierApplications } from '../../../../store/requests/courier';
import { ORDER_TYPES } from '../../../../helpers/constants';
const Edit = React.lazy(() => import('./edit'));
const CourierApplicationsTableFilters = React.lazy(
  () => import('./CourierApplicationsTableFilters'),
);

const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [25, 50, 100],
    size: 'small',
    current: 1,
    pageSize: 25,
  },
};

const CourierApplications = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [tableParams, setTableParams] = useState(initialPageParams);
  const [data, setData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const csvLink = useRef();
  const [downloadedData, setDownloadedData] = useState([]);
  const [exportButtonLoading, setExportButtonLoading] = useState(false);
  const isFirstRender = useRef(true);
  const filterCache = useRef({
    cities: null,
    districts: {},
    neighbourhoods: {},
  });

  const fetchData = async (params = tableParams, currentFilters = filters) => {
    setLoading(true);
    try {
      const queryString = getQueryString(params, currentFilters);
      const response = await getFilteredCourierApplications(queryString);
      const { content, page } = response;

      setData(content);
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          total: page.totalElements,
          current: page.number + 1,
        },
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchCSVData = async () => {
    setExportButtonLoading(true);
    try {
      const queryString = getCSVExportQueryString();
      const response = await getFilteredCourierApplications(queryString);
      const csvData = response.content;

      if (csvData && csvData.length > 0) {
        const exportData = [
          [
            t('fullName'),
            t('status'),
            t('description'),
            t('tcknVkn'),
            t('birthDate'),
            t('gender'),
            t('email'),
            t('phoneNumber'),
            t('vehicleType'),
            t('driverLicense'),
            t('p1AuthorizationCertificate'),
            t('soleProprietorship'),
            t('applicationDate'),
            t('salaryExpectation'),
            t('cityName'),
            t('district'),
            t('neighborhood'),
            t('regionsToBeServed'),
          ],
          ...csvData.map((item) => [
            item.fullName || '-',
            item.status ? t(item.status.toLowerCase()) : '-',
            item.statusDescription || '-',
            item.identityNumber || '-',
            item.birthDate
              ? new Date(item.birthDate).toLocaleDateString('tr-TR')
              : '-',
            item.gender
              ? item.gender === 'MALE'
                ? t('male')
                : t('female')
              : '-',
            item.email || '-',
            item.phoneNumber || '-',
            (() => {
              const vt = item.vehicleTypes ?? item.vehicleType;
              if (!vt) return '-';
              if (Array.isArray(vt)) return vt.map((v) => t(v)).join(', ');
              return t(vt);
            })(),
            item.drivingLicenses
              ? Array.isArray(item.drivingLicenses)
                ? item.drivingLicenses.join(', ')
                : item.drivingLicenses
              : '-',
            item.hasP1 != null ? (item.hasP1 ? t('hasYes') : t('hasNo')) : '-',
            item.hasFirm != null
              ? item.hasFirm
                ? t('hasYes')
                : t('hasNo')
              : '-',
            item.createdAt
              ? new Date(item.createdAt).toLocaleDateString('tr-TR')
              : '-',
            item.earningExpected
              ? Number(item.earningExpected).toLocaleString('tr-TR')
              : '-',
            item.cityName || '-',
            item.districtName || '-',
            item.neighbourhoodName || '-',
            item.serviceDistricts
              ? Array.isArray(item.serviceDistricts)
                ? item.serviceDistricts.join(', ')
                : item.serviceDistricts
              : '-',
          ]),
        ];
        setDownloadedData(exportData);
        csvLink.current.link.click();
      }
    } catch (error) {
      console.error('CSV export error:', error);
    } finally {
      setExportButtonLoading(false);
    }
  };

  const getQueryString = (params = tableParams, currentFilters = filters) => {
    let queryString = '';
    const entries = Object.entries(currentFilters).filter(
      ([, value]) => value != null && value !== undefined && value !== '',
    );
    if (entries.length > 0) {
      const filterStr = entries
        .map(([key, value]) => {
          let field = key;
          if (key === 'cityName') field = 'city.name';
          else if (key === 'neighbourhoodName') field = 'neighbourhood.name';
          else if (key === 'districtName')
            field = 'neighbourhood.district.name';
          else if (key === 'serviceDistricts')
            field = 'serviceDistricts.district.name';

          if (key === 'serviceDistricts' || key === 'status') {
            const vals = Array.isArray(value)
              ? value
              : String(value)
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean);
            if (vals.length === 0) return '';
            const processedVals =
              key === 'status' ? vals.map((v) => v.toUpperCase()) : vals;
            const joined = processedVals.join(',');
            return `${field}=in=(${joined})`;
          }

          const filterValue = value;
          return `${field}=='${filterValue}'`;
        })
        .filter(Boolean)
        .join(' and ');
      queryString += `filter=${filterStr}`;
    }
    queryString = `${queryString}&page=${
      params.pagination.current - 1
    }&size=${params.pagination.pageSize}`;

    if (params.column) {
      queryString = `${queryString}&sort=${params.column.dataIndex},${
        ORDER_TYPES[params.order]
      }`;
    }
    return queryString;
  };

  const getCSVExportQueryString = () => {
    let queryString = '';
    const entries = Object.entries(filters).filter(
      ([, value]) => value != null && value !== undefined && value !== '',
    );
    if (entries.length > 0) {
      const filterStr = entries
        .map(([key, value]) => {
          let field = key;
          if (key === 'cityName') field = 'city.name';
          else if (key === 'neighbourhoodName') field = 'neighbourhood.name';
          else if (key === 'districtName')
            field = 'neighbourhood.district.name';
          else if (key === 'serviceDistricts')
            field = 'serviceDistricts.district.name';

          if (key === 'serviceDistricts' || key === 'status') {
            const vals = Array.isArray(value)
              ? value
              : String(value)
                  .split(',')
                  .map((v) => v.trim())
                  .filter(Boolean);
            if (vals.length === 0) return '';
            const processedVals =
              key === 'status' ? vals.map((v) => v.toUpperCase()) : vals;
            const joined = processedVals.join(',');
            return `${field}=in=(${joined})`;
          }

          const filterValue = value;
          return `${field}=='${filterValue}'`;
        })
        .filter(Boolean)
        .join(' and ');
      queryString += `filter=${filterStr}`;
    }
    queryString = `${queryString}&size=20000`;

    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${
        ORDER_TYPES[tableParams.order]
      }`;
    }
    return queryString;
  };

  useEffect(() => {
    fetchData();
    return () => {
      setData([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const resetParams = {
      ...tableParams,
      pagination: { ...tableParams.pagination, current: 1 },
    };
    setTableParams(resetParams);
    fetchData(resetParams, filters);
    setIsFiltersOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleTableChange = (pagination, _, sorter) => {
    const updatedParams = { pagination, ...sorter };
    setTableParams(updatedParams);
    fetchData(updatedParams, filters);
  };

  const columns = [
    {
      title: t('fullName'),
      dataIndex: 'fullName',
      key: 'fullName',
      width: 200,
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      render: (status) => {
        const statusLower = status ? status.toLowerCase() : null;
        const statusColors = {
          initial: 'green',
          reviewing: 'orange',
          applicable: 'blue',
          not_applicable: 'volcano',
          closed: 'red',
        };
        return (
          <Tag color={statusColors[statusLower] || 'gray'}>
            {statusLower ? t(statusLower) : '-'}
          </Tag>
        );
      },
    },
    {
      title: t('salaryExpectation'),
      dataIndex: 'earningExpected',
      key: 'earningExpected',
      sorter: true,
      showSorterTooltip: false,
      render: (earningExpected) => {
        if (!earningExpected) return '-';
        return Number(earningExpected).toLocaleString('tr-TR');
      },
    },
    {
      title: t('cityName'),
      dataIndex: 'cityName',
      key: 'cityName',
      sorter: true,
      showSorterTooltip: false,
      render: (cityName) => (cityName ? cityName : '-'),
    },
    {
      title: t('district'),
      dataIndex: 'districtName',
      key: 'districtName',
      sorter: true,
      showSorterTooltip: false,
      render: (districtName) => (districtName ? districtName : '-'),
    },
    {
      title: t('neighborhood'),
      dataIndex: 'neighbourhoodName',
      key: 'neighbourhoodName',
      sorter: true,
      showSorterTooltip: false,
      render: (neighbourhoodName) =>
        neighbourhoodName ? neighbourhoodName : '-',
    },
    {
      title: t('regionsToBeServed'),
      dataIndex: 'serviceDistricts',
      key: 'serviceDistricts',
      width: 350,
      sorter: true,
      showSorterTooltip: false,
      render: (serviceDistricts) =>
        serviceDistricts
          ? Array.isArray(serviceDistricts)
            ? serviceDistricts.join(', ')
            : serviceDistricts
          : '-',
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            menu={{
              items: [
                {
                  label: t('showDetails'),
                  icon: <EyeOutlined />,
                  onClick: () => {
                    setIsEditModalOpen(true);
                    setSelectedItem(dataItem);
                  },
                },
              ],
            }}
            placement="bottomRight"
            arrow
            trigger={['click']}
            onClick={() => setSelectedItem(dataItem)}
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        ),
    },
  ];

  const getFilterLabel = (key) => {
    const labelMap = {
      fullName: t('fullName'),
      status: t('status'),
      earningExpected: t('salaryExpectation'),
      cityName: t('cityName'),
      districtName: t('district'),
      neighbourhoodName: t('neighborhood'),
      serviceDistricts: t('regionsToBeServed'),
    };
    return labelMap[key] || t(key);
  };

  const renderSelectedFilters = () => {
    return Object.keys(filters).map((item, idx) => {
      if (filters[item]) {
        let formattedValue = filters[item];
        if (item === 'earningExpected') {
          formattedValue = Number(filters[item]).toLocaleString('tr-TR');
        } else if (item === 'status') {
          formattedValue = Array.isArray(filters[item])
            ? filters[item].map((s) => t(s.toLowerCase())).join(', ')
            : t(filters[item].toLowerCase());
        }
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${getFilterLabel(item)} : ${formattedValue}`}
          </Tag>
        );
      }
      return null;
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filters).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filters,
          [destinationItem]: undefined,
        };
        setFilters(editedFilters);
        form.setFieldsValue(editedFilters);
      }
    });
  };

  const actions = [
    {
      isCrudEnabled: true,
      id: 'downloadButton',
      type: '',
      title: t('download'),
      customContent: (
        <>
          <Button
            shape="round"
            onClick={fetchCSVData}
            icon={<DownloadOutlined />}
            loading={exportButtonLoading}
          />
          <CSVLink
            filename={t('courierApplications')}
            data={downloadedData}
            ref={csvLink}
            className="hidden"
            separator=";"
            uFEFF={true}
          />
        </>
      ),
      disabled: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                style={{ marginBottom: '10px', marginRight: '3px' }}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
        }}
      >
        <div>{renderActionButtons()}</div>
        <Button
          shape="round"
          icon={<FilterOutlined />}
          onClick={() => setIsFiltersOpen(!isFiltersOpen)}
        >
          {t('filters')}
        </Button>
      </Row>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '10px',
        }}
      >
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={data}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) => record.id}
            scroll={{
              y: '70vh',
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <CourierApplicationsTableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            setFilters={setFilters}
            form={form}
            filterCache={filterCache}
          />
        )}
        {isEditModalOpen && (
          <Edit
            selectedCouiersData={selectedItem}
            onCloseHandler={() => setIsEditModalOpen(false)}
            onSuccessCallback={() => fetchData()}
          />
        )}
      </Suspense>
    </div>
  );
};

export default CourierApplications;
